import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Components

import SummaryItem from '../SummaryItem';

// Utils

import { transformDropdownOptionsToObject } from '@utils/normalize/transformDropdownOptionsToObject';
import { questionsVisibility } from '@utils/miscellaneous/pageAndQuestionsVisibility';

// Constants

import { DROPDOWN_OPTIONS} from '@constants/ssap/hhmTypes/dropdownOptions';
import { NO_PRIOR_DATA } from '@constants/ssap/miscellaneous/miscellaneous';

const AdditionalInformationInfo = props => {

    const {
        alwaysDisplayMedicaidQuestions,
        exchangeAndProgramNames,
        idx,
        inlineCurrentIncomeEnabled,
        isState,
        healthCoverage: {
            absentParent,
            applicationSameAsPast3Months,
            eligibleITU,
            monthsRequestingAssistance,
            receivedITU,
            unpaidBill,
        },
        livingArrangement,
        isLivingArrangementShown,
        mcoSelection,
        name,
        isIndianHealthServicesShown,
        preliminaryMedicaidStatus
    } = props;

    const params = {
        alwaysDisplayMedicaidQuestions,
        inlineCurrentIncomeEnabled,
        isState,
        preliminaryMedicaidStatus
    };

    const intApi = useIntl();

    const isMcoSelectionShown = questionsVisibility.isMcoSelectionShown(params);

    const replacements = { personName: normalizeNameOfPerson(name) };

    const options = transformDropdownOptionsToObject(DROPDOWN_OPTIONS.MCO_SELECTIONS);
    const livingArrangementOptions = transformDropdownOptionsToObject(DROPDOWN_OPTIONS.LIVING_ARRANGEMENT);

    const secureValue = value => {
        if(value === undefined) return NO_PRIOR_DATA;
        return value;
    };

    const getMcoSelection = value => {
        return secureValue(options[value]);
    };

    const getLivingArrangementLabel = value => {
        return secureValue(livingArrangementOptions[value]);
    };

    const getMonthsRequestingAssistance = (monthsRequestingAssistance = []) => {

        if(monthsRequestingAssistance.length === 0) {
            return NO_PRIOR_DATA;
        }

        return Object
            .entries(monthsRequestingAssistance)
            .reduce(
                (output, [key, value]) => {
                    let id = 'common.no_text';

                    switch(key) {
                        case 'lastMonth':
                            id = 'ssap.input.label.medical_bills_last_month';
                            break;
                        case 'lastButOneMonth':
                            id = 'ssap.input.label.medical_bills_last_but_one_month';
                            break;
                        case 'threeMonthsAgo':
                            id = 'ssap.input.label.medical_bills_three_months_ago';
                            break;
                    }

                    if(!value) {
                        return output;
                    } else {
                        return [
                            ...output,
                            intApi.formatMessage({ id })
                        ];
                    }
                },
                [ ]
            )
            .join(', ');
    };

    return (
        <Fragment>
            {
                (eligibleITU !== null && isIndianHealthServicesShown) &&

                <SummaryItem
                    category = 'ssap.additional_info.summary.eligible_for_tribal_program'
                    value = { eligibleITU }
                    noPriorDataValues = { [ null ] }
                    desc = {
                        [
                            [
                                `taxHousehold.householdMember[${ idx }].healthCoverage.eligibleITU`
                            ]
                        ]
                    }
                />
            }
            {
                (receivedITU !== null && isIndianHealthServicesShown) &&

                <SummaryItem
                    category = 'ssap.additional_info.summary.services_from_tribal_program'
                    value = { receivedITU }
                    noPriorDataValues = { [ null ] }
                    desc = {
                        [
                            [
                                `taxHousehold.householdMember[${ idx }].healthCoverage.receivedITU`
                            ]
                        ]
                    }
                />
            }
            {
                unpaidBill !== null

                    ? (
                        <Fragment>
                            <SummaryItem
                                category = 'ssap.additional_info.summary.medical_bills'
                                value = { unpaidBill }
                                replacements = { replacements }
                                noPriorDataValues = { [ null ] }
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${ idx }].healthCoverage.unpaidBill`
                                        ]
                                    ]
                                }
                            />

                            {
                                (isState.MN && unpaidBill)
                                    ? (
                                        <Fragment>
                                            <SummaryItem
                                                category = 'ssap.additional_info.select_months_with_medical_bills'
                                                value = { getMonthsRequestingAssistance(monthsRequestingAssistance) }
                                                ignoreIntl
                                                desc = {
                                                    [
                                                        [
                                                            `taxHousehold.householdMember[${ idx }].healthCoverage.monthsRequestingAssistance`,
                                                            getMonthsRequestingAssistance
                                                        ]
                                                    ]
                                                }
                                            />

                                            <SummaryItem
                                                category = 'ssap.input.legend.application_same_as_past_3_months'
                                                value = { applicationSameAsPast3Months }
                                                replacements = { exchangeAndProgramNames }
                                                noPriorDataValues = { [ null ] }
                                                desc = {
                                                    [
                                                        [
                                                            `taxHousehold.householdMember[${ idx }].healthCoverage.applicationSameAsPast3Months`
                                                        ]
                                                    ]
                                                }
                                            />
                                        </Fragment>
                                    )
                                    : null
                            }
                        </Fragment>
                    )
                    : null
            }
            {
                absentParent !== null &&

                <SummaryItem
                    category = 'ssap.additional_info.summary.parents_live_outside'
                    value = { absentParent }
                    noPriorDataValues = { [ null ] }
                    desc = {
                        [
                            [
                                `taxHousehold.householdMember[${ idx }].healthCoverage.absentParent`
                            ]
                        ]
                    }
                />
            }

            {
                isMcoSelectionShown &&

                <SummaryItem
                    category = 'ssap.input.legend.mco_selection'
                    value = { getMcoSelection(mcoSelection) }
                    replacements = { replacements }
                    desc = {
                        [
                            [
                                `taxHousehold.householdMember[${ idx }].mcoSelection`,
                                getMcoSelection
                            ]
                        ]
                    }
                />
            }
            {
                isLivingArrangementShown &&

                <SummaryItem
                    category = 'ssap.input.label.living_arrangement_summary_question'
                    value = { getLivingArrangementLabel(livingArrangement) }
                    desc = {
                        [
                            [
                                `taxHousehold.householdMember[${ idx }].livingArrangement`,
                                getLivingArrangementLabel
                            ]
                        ]
                    }
                />
            }
        </Fragment>
    );
};

export default AdditionalInformationInfo;